import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Modal from '../../../../shared/Modal/Modal';
import VideoPlayer from '../../../../shared/video-player/video-player';
import PreTrainingSurvey from './PreTrainingSurvey';
import {
  deriveLastVisitedRouteFromProgress,
  deriveStartedAdminProgress,
  hasStartedProgramFromProgress,
  isAdminProgramCompleteFromProgress,
  useAdminInitialVideoMutation,
  useAdminProgressQuery,
  useStartAdminProgressMutation,
} from '../../../useAdminProgressQuery';
import { disabledActionAppearanceClassName } from '../FacilitatorHeaderActions';
import { WelcomeVideo } from './WelcomeVideo';
import { hasFullFacilitatorProgramAccessRole } from '../../../../../utils/authRole';

const actionButtonClassName =
  'inline-flex min-w-[190px] items-center justify-center rounded-md border px-4 py-2 text-base font-semibold no-underline transition-colors duration-200';
const primaryButtonClassName = `${actionButtonClassName} border-dark-blue bg-dark-blue text-white hover:border-blue-700 hover:bg-blue-700`;
const disabledButtonClassName = `${actionButtonClassName} ${disabledActionAppearanceClassName}`;
const stackedButtonClassName = 'w-full max-w-md py-3 text-lg';
const techSupportLinkClassName =
  'mt-3 inline-block text-sm font-semibold text-dark-blue underline underline-offset-4 transition-colors duration-200 hover:text-blue-700';

const FACILITATOR_TRAINING_START_ROUTE =
  '/facilitator/introduction/module-objectives';
const FACILITATOR_PARENT_PREVIEW_ROUTE = '/facilitator/parent-preview';
const FACILITATOR_TECH_SUPPORT_URL =
  'https://osu.az1.qualtrics.com/jfe/form/SV_eqy7DOElHo3rYh0';
const FACILITATOR_INITIAL_VIDEO_SOURCE = {
  en: 1198793103,
  es: 1198793103,
};

const HomeWelcomeMessage = ({ title, facilitatorName, description = '' }) => (
  <div className='flex w-full max-w-2xl flex-col items-center gap-3 px-4 py-3 text-center'>
    <h1 className='text-3xl font-normal leading-tight text-dark-blue md:text-4xl mb-0'>
      {title},{' '}
      <span className='font-semibold text-purple'>{facilitatorName}</span>
    </h1>
    {description ? (
      <p className='max-w-xl text-sm leading-6 text-slate-600 md:text-base'>
        {description}
      </p>
    ) : null}
  </div>
);

const HomePAge = () => {
  const history = useHistory();
  const { adminProgress, hasLoadedAdminProgress } = useAdminProgressQuery({
    currentPath: '',
  });
  const initialVideoMutation = useAdminInitialVideoMutation();
  const startTrainingMutation = useStartAdminProgressMutation();
  const [showPreTrainingSurvey, setShowPreTrainingSurvey] = useState(false);
  const [hasCompletedInitialVideo, setHasCompletedInitialVideo] =
    useState(false);
  const facilitatorName = localStorage.getItem('name') || 'there';
  const hasFullFacilitatorProgramAccess =
    hasFullFacilitatorProgramAccessRole(localStorage.getItem('role'));
  const dashboardLabelPrefix = hasFullFacilitatorProgramAccess
    ? 'Admin'
    : 'Facilitator';

  const hasStartedProgram = hasStartedProgramFromProgress(adminProgress);
  const isProgramComplete =
    hasLoadedAdminProgress &&
    (hasFullFacilitatorProgramAccess ||
      isAdminProgramCompleteFromProgress(adminProgress));
  const initialVideoWatched = adminProgress.initialVideoWatched;
  const preSurveyCompleted = adminProgress.preSurveyCompleted;
  const canAccessEzParent =
    hasFullFacilitatorProgramAccess || adminProgress.postSurveyCompleted;
  const canAccessDashboard =
    hasFullFacilitatorProgramAccess || adminProgress.postSurveyCompleted;
  const showWelcomeBackState =
    (hasFullFacilitatorProgramAccess || initialVideoWatched) &&
    hasStartedProgram &&
    !isProgramComplete;
  const showWelcomeScreen = !showWelcomeBackState && !isProgramComplete;
  const canStartTraining =
    hasFullFacilitatorProgramAccess ||
    initialVideoWatched ||
    hasCompletedInitialVideo;
  const continueRoute = hasStartedProgram
    ? deriveLastVisitedRouteFromProgress(adminProgress)
    : FACILITATOR_TRAINING_START_ROUTE;

  useEffect(() => {
    if (
      !hasLoadedAdminProgress ||
      !showWelcomeScreen ||
      preSurveyCompleted ||
      hasFullFacilitatorProgramAccess
    ) {
      setShowPreTrainingSurvey(false);
      return;
    }

    setShowPreTrainingSurvey(true);
  }, [
    hasFullFacilitatorProgramAccess,
    hasLoadedAdminProgress,
    preSurveyCompleted,
    showWelcomeScreen,
  ]);

  const handleVideoWatchedChange = () => {
    setHasCompletedInitialVideo(true);
  };

  const handleStartTraining = () => {
    if (!preSurveyCompleted && !hasFullFacilitatorProgramAccess) {
      setShowPreTrainingSurvey(true);
      return;
    }

    const nextInitialVideoWatched =
      hasFullFacilitatorProgramAccess ||
      initialVideoWatched ||
      hasCompletedInitialVideo;
    const startedProgress = {
      ...deriveStartedAdminProgress(adminProgress),
      initialVideoWatched: nextInitialVideoWatched,
    };

    if (
      !hasFullFacilitatorProgramAccess &&
      !initialVideoWatched &&
      hasCompletedInitialVideo
    ) {
      initialVideoMutation.mutate();
    }

    startTrainingMutation.mutate(startedProgress);
    history.push(FACILITATOR_TRAINING_START_ROUTE);
  };

  if (!hasLoadedAdminProgress) {
    return (
      <div className='flex min-h-full w-full items-center justify-center p-6'>
        <div className='h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-dark-blue' />
      </div>
    );
  }

  return (
    <div className='text-center p-4 h-full w-full relative overflow-auto'>
      <Modal
        noPortal
        show={showPreTrainingSurvey}
        showModal={setShowPreTrainingSurvey}
        disableDismiss
      >
        <PreTrainingSurvey
          closeModal={() => {
            setShowPreTrainingSurvey(false);
          }}
        />
      </Modal>
      <div className='relative h-full w-full p-2'>
        <img
          className='absolute top-0 left-0 w-full h-full object-cover z-0 opacity-50 '
          src='/homeimg.jpg'
          alt=''
        />
        <div className='relative z-10 max-w-4xl mx-auto space-y-4 py-5'>
          <div className='bg-white/95 rounded-lg shadow-md border border-gray-300 p-5 md:p-6 space-y-5'>
            {showWelcomeScreen && (
              <div className='mx-auto flex w-full max-w-3xl flex-col items-center gap-5 py-4'>
                <HomeWelcomeMessage
                  title={
                    <>
                      Welcome to <i>ez</i>Training
                    </>
                  }
                  facilitatorName={facilitatorName}
                  description='Watch the video below to learn more about the program and how to get started. Once you are ready, click the button below to begin your training.'
                />
                <div className='w-full max-w-3xl overflow-hidden rounded-lg border border-gray-300 bg-black shadow-sm'>
                  <div className='aspect-video w-full'>
                    <WelcomeVideo setVideoWatched={handleVideoWatchedChange} />
                  </div>
                </div>
                <div className='flex w-full max-w-2xl flex-col items-center gap-2 px-4 pt-2'>
                  <div className='flex w-full max-w-md flex-col items-stretch gap-3'>
                    <button
                      type='button'
                      onClick={handleStartTraining}
                      disabled={!canStartTraining}
                      className={
                        !canStartTraining
                          ? `${disabledButtonClassName} ${stackedButtonClassName}`
                          : `${primaryButtonClassName} ${stackedButtonClassName}`
                      }
                    >
                      <span>
                        Start <i>ez</i>Training
                      </span>
                    </button>
                    <Link
                      to='/facilitator/resource-library'
                      className={`${primaryButtonClassName} ${stackedButtonClassName}`}
                    >
                      <span>
                        <i>ez</i>Resources
                      </span>
                    </Link>
                  </div>
                  <a
                    href={FACILITATOR_TECH_SUPPORT_URL}
                    target='_blank'
                    rel='noreferrer'
                    className={techSupportLinkClassName}
                  >
                    Need Tech Support
                  </a>
                </div>
              </div>
            )}
            {showWelcomeBackState && (
              <div className='mx-auto flex w-full max-w-3xl flex-col items-center gap-5 py-4'>
                <HomeWelcomeMessage
                  title='Welcome back'
                  facilitatorName={facilitatorName}
                  description={
                    <>
                      Pick up where you left off or view <i>ez</i>Resources.
                    </>
                  }
                />
                <div className='flex w-full max-w-2xl flex-col items-center gap-2 px-4 pt-2'>
                  <div className='flex w-full max-w-md flex-col items-stretch gap-3'>
                    <Link
                      to={continueRoute}
                      className={`${primaryButtonClassName} ${stackedButtonClassName}`}
                    >
                      Continue where you left off
                    </Link>
                    <Link
                      to='/facilitator/resource-library'
                      className={`${primaryButtonClassName} ${stackedButtonClassName}`}
                    >
                      <span>
                        <i>ez</i>Resources
                      </span>
                    </Link>
                  </div>
                  <a
                    href={FACILITATOR_TECH_SUPPORT_URL}
                    target='_blank'
                    rel='noreferrer'
                    className={techSupportLinkClassName}
                  >
                    Need Tech Support
                  </a>
                </div>
              </div>
            )}
            {isProgramComplete && (
              <div className='mx-auto flex w-full max-w-3xl flex-col items-center gap-5 py-4'>
                <HomeWelcomeMessage
                  title='Welcome back'
                  facilitatorName={facilitatorName}
                  description={
                    <>
                      Your training is complete. Use these buttons to revisit{' '}
                      <i>ez</i>Training, view facilitator resources, or access{' '}
                      <i>ez</i>Parent and the <i>ez</i>Dashboard.
                    </>
                  }
                />
                <div className='flex w-full max-w-2xl flex-col items-center gap-2 px-4 pt-2'>
                  <div className='flex w-full max-w-md flex-col items-stretch gap-3'>
                    <Link
                      to={continueRoute}
                      className={`${primaryButtonClassName} ${stackedButtonClassName}`}
                    >
                      <span>
                        <i>ez</i>Training
                      </span>
                    </Link>
                    <Link
                      to='/facilitator/resource-library'
                      className={`${primaryButtonClassName} ${stackedButtonClassName}`}
                    >
                      <span>
                        <i>ez</i>Resources
                      </span>
                    </Link>
                    {canAccessEzParent ? (
                      <Link
                        to={FACILITATOR_PARENT_PREVIEW_ROUTE}
                        target='_blank'
                        rel='noreferrer'
                        className={`${primaryButtonClassName} ${stackedButtonClassName}`}
                      >
                        <span>
                          <i>ez</i>Parent
                        </span>
                      </Link>
                    ) : (
                      <button
                        type='button'
                        disabled
                        className={`${disabledButtonClassName} ${stackedButtonClassName}`}
                      >
                        <span>
                          <i>ez</i>Parent
                        </span>
                      </button>
                    )}
                    {canAccessDashboard ? (
                      <Link
                        to='/facilitator/dashboard'
                        className={`${primaryButtonClassName} ${stackedButtonClassName}`}
                      >
                        <span>
                          {dashboardLabelPrefix} <i>ez</i>Dashboard
                        </span>
                      </Link>
                    ) : (
                      <button
                        type='button'
                        disabled
                        className={`${disabledButtonClassName} ${stackedButtonClassName}`}
                      >
                        <span>
                          {dashboardLabelPrefix} <i>ez</i>Dashboard
                        </span>
                      </button>
                    )}
                  </div>
                  <a
                    href={FACILITATOR_TECH_SUPPORT_URL}
                    target='_blank'
                    rel='noreferrer'
                    className={techSupportLinkClassName}
                  >
                    Need Tech Support
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePAge;
